import React, { useState } from 'react';
import { process } from '@progress/kendo-data-query';
import { locale } from '../../common/localization/localizationService';
import ColumnMenu from '../shared/ColumnMenu';
import {
  Grid,
  GridColumn,
  GridColumnMenuFilter,
} from '@progress/kendo-react-grid';
import routes from '../../common/routes';
import { getExecutedIcon } from '../../utils/GeneralUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ICustomerLog } from '../../models/CustomerLogs.model';
import { Link } from 'react-router-dom';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import {
  getDeviations,
  getLogData,
  getPictures,
} from '../shared/routeStopUtility';
import { getDataButtonValues } from '../shared/dataButtonUtility';
import parser from 'fast-xml-parser';

export interface ICustomerLogsGridProps {
  data: Array<ICustomerLog>;
  className?: string;
  dataButtons: Array<any>;
  setSelectedLogline: (logLine: ICustomerLog) => void;
}

const CustomerLogsGrid: React.FC<ICustomerLogsGridProps> = ({
  data,
  className,
  dataButtons,
  setSelectedLogline,
}) => {
  const [dataState, setDataState] = useState({
    sort: [{ dir: 'desc', field: 'time' }],
    take: 26,
    skip: 0,
  });
  const dataStateChange = (event: any) => {
    setDataState(event.data);
  };

  const columnProps = (field: string) => {
    return {
      field: field,
      columnMenu: ColumnMenu,
      headerClassName: isColumnActive(field, dataState) ? 'active' : '',
    };
  };

  const isColumnActive = (field: string, dataState: any) => {
    return GridColumnMenuFilter.active(field, dataState.filter);
  };

  const infoButtonCell = (columnProps: any) => {
    return (
      <td
        className={`action-button ${className}`}
        onClick={() => setCustomerLog(columnProps.dataItem)}
      >
        <Link to={routes.customerLogs.customerLogDetail}>
          <FontAwesomeIcon icon={faInfoCircle} />
        </Link>
      </td>
    );
  };

  const setCustomerLog = (customerLog: ICustomerLog): void => {
    setSelectedLogline(customerLog);
  };

  const getDeviationPictures = (logData: any): Array<string> => {
    let pictures;
    try {
      pictures = getPictures(logData);
    } catch {
      pictures = Array.isArray(logData.PicURL)
        ? logData.PicURL
        : [logData.PicURL];
    }
    return pictures;
  };

  const gridData = data.map((log) => {
    const logData = getLogData(log);
    log.time = new Date(log.time);
    if (logData) {
      log.comment = logData.Comment;
      log.driver = logData.DriverId;
      log.deviationPicture = getDeviationPictures(logData);
      log.unitDeviations = getDeviations(logData);
      log.dataButtonValues = getDataButtonValues(logData, dataButtons);
    }
    return log;
  });
  const result = process(gridData, dataState);

  return (
    <>
      <Grid
        {...dataState}
        className={`customer-logs-grid ${className}`}
        data={result}
        selectedField="selected"
        pageable
        resizable
        pageSize={26}
        onDataStateChange={dataStateChange}
        sortable
      >
        <GridColumn
          {...columnProps('time')}
          title={locale.customerLogs._arrivalTime}
          format={'{0:' + locale.general._fullDateWithTime + '}'}
          width={150}
          sortable
          filter="date"
        />
        <GridColumn
          width={80}
          title={locale.customerLogs._executed}
          cell={(props) => {
            return (
              <td className="executed-icon-cell">
                {
                  <FontAwesomeIcon
                    icon={getExecutedIcon(props.dataItem.message)}
                  />
                }
              </td>
            );
          }}
        />
        <GridColumn
          {...columnProps('vehicleId')}
          title={locale.customerLogs._vehicle}
          sortable
          filter="text"
          width={100}
        />
        <GridColumn
          {...columnProps('driver')}
          title={locale.customerLogs._driver}
          sortable
          filter="text"
          width={100}
        />
        <GridColumn
          {...columnProps('agreementId')}
          title={locale.customerLogs._agreementId}
          sortable
          filter="numeric"
          width={100}
        />
        <GridColumn
          {...columnProps('name')}
          title={locale.customerLogs._name}
          sortable
          filter="text"
          width={180}
        />
        <GridColumn
          {...columnProps('estate')}
          title={locale.customerLogs._address}
          sortable
          filter="text"
        />
        <GridColumn
          {...columnProps('place')}
          title={locale.customerLogs._place}
          sortable
          filter="text"
        />
        <GridColumn
          {...columnProps('unitName')}
          title={locale.customerLogs._item}
          sortable
          filter="text"
          width={175}
        />
        <GridColumn
          {...columnProps('unitDeviations')}
          title={locale.customerLogs._deviation}
          sortable
          filter="text"
          width={200}
        />
        <GridColumn
          {...columnProps('comment')}
          title={locale.customerLogs._comment}
          sortable
          filter="text"
          width={150}
        />
        <GridColumn
          {...columnProps('paSystem')}
          title={locale.customerLogs._paSystem}
          sortable
          filter="text"
          width={100}
        />
        <GridColumn
          {...columnProps('externalOrderId')}
          title={locale.customerLogs._externalOrderId}
          sortable
          filter="numeric"
          width={100}
        />
        <GridColumn
          {...columnProps('logLineId')}
          title={locale.customerLogs._logLineId}
          sortable
          filter="numeric"
          width={100}
        />
        <GridColumn width="40px" cell={infoButtonCell} />
      </Grid>
    </>
  );
};

export default CustomerLogsGrid;
